export interface DecisionInput {
  matchId: string;
  version: number;
  decisionPlayer: number;
  humanPlayer: number;
  finished: boolean;
}

export type DecisionDelivery = 'commit' | 'resume' | 'new';

/** Tracks priority changes, independently of animation events or audio availability. */
export class DecisionNotification {
  private previous?: DecisionInput;
  private queued = false;

  reset(): void {
    this.previous = undefined;
    this.queued = false;
  }

  observe(input: DecisionInput, delivery: DecisionDelivery): void {
    const previous = this.previous;
    const changedMatch = !previous || previous.matchId !== input.matchId || previous.humanPlayer !== input.humanPlayer;
    if (changedMatch) {
      this.previous = { ...input };
      this.queued = delivery === 'new' && this.isMine(input);
      return;
    }
    // A transport resume is a fresh baseline, even when its version did not advance.
    // Never replay an outstanding notification across a disconnection.
    if (delivery === 'resume') {
      this.queued = false;
      if (input.version >= previous.version) this.previous = { ...input };
      return;
    }
    if (input.finished) this.queued = false;
    if (input.version <= previous.version) return;
    this.previous = { ...input };
    if (!this.isMine(input)) {
      this.queued = false;
    } else if (!previous.finished && previous.decisionPlayer !== input.humanPlayer) {
      this.queued = true;
    }
    // Consecutive decisions by the same seat retain at most the original pending cue.
  }

  /** Call once presentation is ready; consume even if audio is muted or suspended. */
  take(current: DecisionInput): boolean {
    const previous = this.previous;
    if (!previous || previous.matchId !== current.matchId || previous.humanPlayer !== current.humanPlayer || !this.isMine(current)) {
      this.queued = false;
      return false;
    }
    if (current.version !== previous.version || current.decisionPlayer !== previous.decisionPlayer) return false;
    const queued = this.queued;
    this.queued = false;
    return queued;
  }

  private isMine(input: DecisionInput): boolean {
    return !input.finished && input.decisionPlayer === input.humanPlayer;
  }
}
